import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState, type JSX } from 'react';
import { refreshOnce } from './api/client';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import ErrorBoundary from './components/ErrorBoundary';

// Route-level code splitting: každá stránka je vlastní chunk. Anonymní návštěvník
// veřejného kola (/w/:slug) tak nestáhne editor ani admin bundle. RequireAdmin necháváme
// eager — je to malý guard používaný v layoutu.
import RequireAdmin from './components/admin/RequireAdmin';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const WheelListPage = lazy(() => import('./pages/WheelListPage'));
const WheelEditorPage = lazy(() => import('./pages/WheelEditorPage'));
const PlayPage = lazy(() => import('./pages/PlayPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const MediaLibraryPage = lazy(() => import('./pages/MediaLibraryPage'));
const PublicPlayPage = lazy(() => import('./pages/PublicPlayPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminStatsPage = lazy(() => import('./pages/admin/AdminStatsPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));

/**
 * Po refresh stránky se Zustand store vyresetuje. Boot guard provede silent refresh
 * (z HttpOnly cookie) PŘED vykreslením Routes — bez něj by každá `ProtectedRoute`
 * zablikala "…" během fetch. Single attempt na úrovni App.
 *
 * Reusuje `refreshOnce` z api/clienta → sdílí timeout (app nezůstane viset na "…",
 * když /auth/refresh hangne) i cross-tab serializaci (navigator.locks + BroadcastChannel).
 */
function useBootRefresh(): boolean {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [bootDone, setBootDone] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setBootDone(true);
      return;
    }
    let cancelled = false;
    refreshOnce().finally(() => {
      if (!cancelled) setBootDone(true);
    });
    return () => { cancelled = true; };
    // Boot je single-shot; pokud user mezitím loguje, isAuthenticated se sám překlopí.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return bootDone;
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const applyTheme = useThemeStore((s) => s.applyDocumentClass);
  const bootDone = useBootRefresh();

  useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  if (!bootDone) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-neon-cyan flex items-center justify-center">
        <span className="opacity-60 animate-pulse">…</span>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/confirm-email" element={<ConfirmEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <WheelListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/wheels/new"
          element={
            <ProtectedRoute>
              <WheelEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/wheels/:id/edit"
          element={
            <ProtectedRoute>
              <WheelEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/wheels/:id/play"
          element={
            <ProtectedRoute>
              <PlayPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/wheels/:id/history"
          element={
            <ProtectedRoute>
              <HistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/media"
          element={
            <ProtectedRoute>
              <MediaLibraryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        {/* Veřejné kolo — anonymní přístup */}
        <Route path="/w/:slug" element={<PublicPlayPage />} />

        {/* Admin (Sprint 8) — role-guarded */}
        <Route
          path="/app/admin"
          element={
            <ProtectedRoute>
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminStatsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="users" element={<AdminUsersPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

/** Fallback během načítání lazy route chunku — sjednocený s boot loaderem. */
function RouteFallback() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-neon-cyan flex items-center justify-center">
      <span className="opacity-60 animate-pulse">…</span>
    </div>
  );
}
